import React, { useEffect } from 'react';
import { Alert, MenuItem, Stack, Typography } from '@mui/material';
import { format, formatDistanceToNow, isPast } from 'date-fns';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';

import { fetchOrgMembers, updateOrgMember } from 'js/actions';
import { UPDATE_ORG_MEMBER_REQUEST, UPDATE_USER_INFO_REQUEST } from 'js/constants/actionTypes';
import useAppDispatch from 'js/hooks/useAppDispatch';
import { getUserRoleLabel, USER_ROLE_OPTIONS } from 'js/constants/userRoles';
import { isValidEmail } from 'js/lib/ValidationUtils';
import { getCurrentOrg } from 'js/reducers/session';
import { AdminDurationSelect } from 'new/components/settings/Members/AdminDurationSelect';
import { ProfileImage } from 'new/elements/ProfileImage';
import { Select } from 'new/elements/Select';
import { TextField } from 'new/elements/TextField';
import { useCurrentOrg } from 'new/hooks/adapters/useCurrentOrg';
import useDidUpdateEffect from 'new/hooks/useDidUpdateEffect';
import { useModals } from 'new/hooks/useModals';
import { ModalOutProps } from 'new/routes/ModalDefault';
import { ModalComponent } from 'new/routes/RouterManager';
import { showAlert } from 'new/stores/showAlert/action';

import styles from './Members.module.scss';

interface EditUserProps {
  id: number;
  full_name: string;
  email: string;
  access_role: string;
  access_role_expiration_seconds?: number | null;
  access_role_expires_at?: string | null;
}

function AdminExpirationNotice({
  expiresAt,
  roleLabel = 'Admin',
}: {
  expiresAt: string;
  roleLabel?: string;
}) {
  const date = new Date(expiresAt);
  const expired = isPast(date);
  const tzAbbr =
    new Intl.DateTimeFormat(undefined, { timeZoneName: 'short' })
      .formatToParts(date)
      .find((p) => p.type === 'timeZoneName')?.value ?? '';
  const formatted = `${format(date, 'MMM d, yyyy h:mm a')}${tzAbbr ? ` ${tzAbbr}` : ''}`;
  const relative = formatDistanceToNow(date, { addSuffix: true });

  return (
    <Alert severity={expired ? 'error' : 'info'} sx={{ py: 0.5 }}>
      <Typography variant="body2">
        {expired
          ? `${roleLabel} access expired ${relative} (${formatted})`
          : `${roleLabel} access expires ${relative} — ${formatted}`}
      </Typography>
    </Alert>
  );
}

const Role = (item: (typeof USER_ROLE_OPTIONS)[number]) => {
  return (
    <MenuItem key={item.value} className={styles.selectOption} value={item.value}>
      <Typography>{item.title}</Typography>
      <Typography variant="caption">{item.description}</Typography>
    </MenuItem>
  );
};

export const EditUser: ModalComponent = (props: EditUserProps & ModalOutProps) => {
  const {
    register,
    handleSubmit,
    control,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      full_name: props.full_name,
      email: props.email,
      access_role: props.access_role,
      access_role_expiration_seconds: props.access_role_expiration_seconds ?? null,
    },
  });

  const { closeModal } = useModals();
  const dispatch = useAppDispatch();
  const org = useCurrentOrg();
  const currentRole = watch('access_role');
  const isOwner = org?.owner != null && props.id === org.owner && props.email === 'admin@nexla.com';
  const loading = useSelector(
    (state: any) =>
      state.request[UPDATE_ORG_MEMBER_REQUEST] || state.request[UPDATE_USER_INFO_REQUEST],
  );
  const currentOrg = useSelector(getCurrentOrg);

  const onSubmit = (data) => {
    const payload: any = {
      full_name: data.full_name,
      email: data.email,
      access_role: data.access_role,
    };
    if (data.access_role_expiration_seconds != null) {
      payload.access_role_expiration_seconds = data.access_role_expiration_seconds;
    }
    dispatch(updateOrgMember(currentOrg.id, payload)).then(() => {
      dispatch(fetchOrgMembers(currentOrg.id));
      closeModal(EditUser.modalConfig.name);
      dispatch(
        showAlert({
          title: 'Info successfully updated',
          severity: 'success',
        }),
      );
    });
  };

  useDidUpdateEffect(() => {
    props.setLoading(loading);
  }, [loading]);

  useEffect(() => {
    props.setModalActions({
      ok: {
        text: 'Save',
        onClick: handleSubmit(onSubmit),
      },
    });
  }, []);

  return (
    <Stack direction="column" gap={'16px'} minWidth={300}>
      <ProfileImage fullName={props.full_name} className={styles.editUserAvatar} />
      <TextField label="Name" {...register('full_name')} />
      <TextField
        label="Email"
        error={!!errors.email}
        disabled
        helperText={errors.email ? <>{errors.email?.message}</> : undefined}
        {...register('email', {
          validate: (email) => {
            return isValidEmail(email) || 'Email is not valid';
          },
        })}
      />
      <Select
        label="Role"
        fullWidth
        items={USER_ROLE_OPTIONS}
        defaultValue={props.access_role}
        renderItem={Role}
        disabled={isOwner}
        error={!!errors?.access_role}
        helperText={
          isOwner
            ? 'Org owner roles cannot be changed'
            : !!errors?.access_role
              ? errors?.access_role?.message
              : undefined
        }
        renderValue={(value) => (
          <Typography>{USER_ROLE_OPTIONS.find((role) => role.value === value)?.title}</Typography>
        )}
        {...register(`access_role`, {
          required: 'Please select a role for the user',
        })}
      />
      {props.access_role_expires_at && (
        <AdminExpirationNotice
          expiresAt={props.access_role_expires_at}
          roleLabel={getUserRoleLabel(props.access_role)}
        />
      )}
      {!isOwner && (
        <AdminDurationSelect
          control={control}
          setValue={setValue}
          roleFieldName="access_role"
          expirationFieldName="access_role_expiration_seconds"
          initialExpiresAt={props.access_role_expires_at}
          label={currentRole === props.access_role ? 'Update duration' : 'Select duration'}
        />
      )}
    </Stack>
  );
};

EditUser.modalConfig = {
  name: 'members.EditUser',
  title: 'Edit user',
};
